import { Link } from "react-router-dom";
import VerdictBadge from "@/components/feature/VerdictBadge";
import type { Article } from "@/mocks/articles";

interface Props {
  article: Article;
  variant?: "grid" | "list";
}

export function formatHeat(heat: number) {
  if (heat >= 10000) return `${(heat / 10000).toFixed(1)}万`;
  return `${heat}`;
}

export default function ArticleCard({ article, variant = "grid" }: Props) {
  if (variant === "list") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-background-100 md:p-4"
      >
        <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-24 md:w-36">
          <img
            src={article.cover}
            alt={article.coverAlt}
            className="h-full w-full object-top"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <VerdictBadge verdict={article.verdict} />
            <span className="text-xs text-foreground-500">{article.category}</span>
          </div>
          <h3 className="line-clamp-2 font-heading text-sm font-600 leading-snug text-foreground-950 transition-colors group-hover:text-accent-700 md:text-base">
            {article.title}
          </h3>
          <div className="mt-auto flex items-center gap-3 pt-2 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500"></i>
              {formatHeat(article.heat)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="h-44 w-full overflow-hidden bg-background-200 md:h-48">
        <img
          src={article.cover}
          alt={article.coverAlt}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-[1.04]"
        />
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <VerdictBadge verdict={article.verdict} />
          <span className="rounded-full bg-background-100 px-2 py-0.5 text-xs text-foreground-600">
            {article.category}
          </span>
        </div>
        <h3 className="line-clamp-2 font-heading text-base font-600 leading-snug text-foreground-950 transition-colors group-hover:text-accent-700 md:text-lg">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
          <span>{article.date}</span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500"></i>
              {formatHeat(article.heat)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
}